// /billing — staff override surface for plans + credits + reconciliation.
//
// Single page, three sections (no tabs — they're related enough that
// scrolling reads more naturally):
//   1. Subscriptions table with filters + per-row actions (change plan,
//      grant credit) via dialogs.
//   2. Reconciliation runs (last 30 cross-tenant, latest first).
//   3. Drift heatmap (placeholder until P5).

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, GanttChart } from 'lucide-react';
import { toast } from 'sonner';
import { useApiClient } from '../../lib/providers.js';
import {
  RECONCILIATION_QUERY_KEY,
  SUBSCRIPTIONS_QUERY_KEY,
  billingApi,
  type ListSubscriptionsParams,
  type SubscriptionFreezeFilter,
  type SubscriptionPlanFilter,
  type SubscriptionRow,
  type SubscriptionStatusFilter,
} from '../../lib/api/billing.js';
import { Badge } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog.js';
import { Input } from '../../components/ui/input.js';
import { Label } from '../../components/ui/label.js';
import { Select } from '../../components/ui/select.js';
import { Textarea } from '../../components/ui/textarea.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import { EmptyState } from '../../components/ui/empty-state.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { formatUsd } from '../../lib/format-money.js';
import { formatDate, formatDateTime } from '../../lib/format-date.js';

const PLAN_VALUES: SubscriptionPlanFilter[] = ['all', 'free', 'starter', 'pro'];
const FREEZE_VALUES: SubscriptionFreezeFilter[] = ['all', 'active', 'frozen'];
const STATUS_VALUES: SubscriptionStatusFilter[] = ['all', 'active', 'past_due', 'canceled'];

const PLAN_CODES = ['free', 'starter', 'pro'] as const;

export function BillingPage() {
  const client = useApiClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: ListSubscriptionsParams = useMemo(
    () => ({
      plan: (searchParams.get('plan') as SubscriptionPlanFilter | null) ?? 'all',
      freeze: (searchParams.get('freeze') as SubscriptionFreezeFilter | null) ?? 'all',
      status: (searchParams.get('status') as SubscriptionStatusFilter | null) ?? 'all',
      limit: 100,
    }),
    [searchParams],
  );

  const subsQuery = useQuery({
    queryKey: [...SUBSCRIPTIONS_QUERY_KEY, filters] as const,
    queryFn: () => billingApi.listSubscriptions(client, filters),
  });

  const reconQuery = useQuery({
    queryKey: RECONCILIATION_QUERY_KEY,
    queryFn: () => billingApi.listReconciliation(client, { limit: 30 }),
  });

  function setFilter(key: string, value: string | null): void {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === null) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Billing & credits"
        description="Operator override for subscriptions. Plan changes update our DB only — Stripe-side reconciliation lands in P5 (/stripe/divergence)."
      />

      {/* Filter bar */}
      <div className="flex flex-col gap-3 rounded-lg border bg-card p-3 sm:flex-row sm:items-end">
        <FilterSelect
          label="Plan"
          value={filters.plan ?? 'all'}
          values={PLAN_VALUES}
          onChange={(v) => {
            setFilter('plan', v === 'all' ? null : v);
          }}
        />
        <FilterSelect
          label="Freeze"
          value={filters.freeze ?? 'all'}
          values={FREEZE_VALUES}
          onChange={(v) => {
            setFilter('freeze', v === 'all' ? null : v);
          }}
        />
        <FilterSelect
          label="Status"
          value={filters.status ?? 'all'}
          values={STATUS_VALUES}
          onChange={(v) => {
            setFilter('status', v === 'all' ? null : v);
          }}
        />
      </div>

      {/* Subscriptions table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Subscriptions</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {subsQuery.isPending ? (
            <SubsSkeleton />
          ) : subsQuery.isError ? (
            <div className="px-4">
              <ErrorRetry
                title="Failed to load subscriptions"
                error={subsQuery.error}
                onRetry={() => {
                  void subsQuery.refetch();
                }}
              />
            </div>
          ) : subsQuery.data.rows.length === 0 ? (
            <div className="px-4">
              <EmptyState
                title="No subscriptions match"
                description="Loosen the filters or clear them."
              />
            </div>
          ) : (
            <SubsTable rows={subsQuery.data.rows} />
          )}
        </CardContent>
      </Card>

      {/* Reconciliation runs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Reconciliation runs (last 30)</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {reconQuery.isPending ? (
            <ReconSkeleton />
          ) : reconQuery.isError ? (
            <div className="px-4">
              <ErrorRetry
                title="Failed to load reconciliation runs"
                error={reconQuery.error}
                onRetry={() => {
                  void reconQuery.refetch();
                }}
              />
            </div>
          ) : reconQuery.data.rows.length === 0 ? (
            <div className="px-4">
              <EmptyState
                title="No reconciliation runs yet"
                description="The recon job runs daily once Stripe is provisioned. /stripe/reconciliation in P5 will surface a heatmap and a manual-trigger button."
              />
            </div>
          ) : (
            <ReconTable rows={reconQuery.data.rows} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function FilterSelect<T extends string>({
  label,
  value,
  values,
  onChange,
}: {
  label: string;
  value: T;
  values: readonly T[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5 sm:w-40">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select
        value={value}
        onChange={(e) => {
          onChange(e.target.value as T);
        }}
      >
        {values.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </Select>
    </div>
  );
}

function SubsTable({ rows }: { rows: readonly SubscriptionRow[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tenant</TableHead>
          <TableHead>Plan</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">This period</TableHead>
          <TableHead className="text-right">Credits</TableHead>
          <TableHead>Period</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.tenantId}>
            <TableCell>
              <Link to={`/tenants/${row.tenantId}`} className="hover:text-foreground">
                <div className="font-medium">{row.tenantName}</div>
                <code className="text-xs text-muted-foreground">{row.tenantId.slice(0, 8)}…</code>
              </Link>
            </TableCell>
            <TableCell>
              <Badge variant="secondary">{row.planCode}</Badge>
            </TableCell>
            <TableCell>
              <StatusBadge row={row} />
            </TableCell>
            <TableCell className="text-right font-mono text-sm">
              {formatUsd(row.costThisPeriodUsd)}
            </TableCell>
            <TableCell className="text-right font-mono text-sm">
              {row.creditGrantedMicros > 0
                ? formatUsd(row.creditGrantedMicros / 1_000_000)
                : '—'}
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {formatDate(row.currentPeriodStart)} → {formatDate(row.currentPeriodEnd)}
            </TableCell>
            <TableCell className="text-right">
              <div className="inline-flex gap-2">
                <ChangePlanDialog row={row} />
                <GrantCreditDialog row={row} />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function StatusBadge({ row }: { row: SubscriptionRow }) {
  if (row.frozenAt) {
    return (
      <Badge variant="destructive" title={row.frozenReason ?? 'Frozen'}>
        frozen
      </Badge>
    );
  }
  switch (row.status) {
    case 'active':
      return <Badge variant="default">active</Badge>;
    case 'past_due':
      return <Badge variant="destructive">past due</Badge>;
    case 'canceled':
      return <Badge variant="outline">canceled</Badge>;
    default:
      return <Badge variant="outline">{row.status}</Badge>;
  }
}

function ChangePlanDialog({ row }: { row: SubscriptionRow }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [planCode, setPlanCode] = useState(row.planCode);
  const mut = useMutation({
    mutationFn: (next: string) => billingApi.changePlan(client, row.tenantId, next),
    onSuccess: () => {
      toast.success('Plan changed');
      void qc.invalidateQueries({ queryKey: SUBSCRIPTIONS_QUERY_KEY });
      setOpen(false);
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Plan change failed');
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setOpen(true);
        }}
      >
        <GanttChart aria-hidden className="mr-1 h-3.5 w-3.5" />
        Plan
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change plan for {row.tenantName}</DialogTitle>
          <DialogDescription>
            This updates our DB only — Stripe-side state stays where it is until
            the divergence detector reconciles. The change is recorded in
            <code> staff_audit_log</code> with a <code>stripeSyncDeferred</code> flag.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor={`plan-${row.tenantId}`}>Plan</Label>
          <Select
            id={`plan-${row.tenantId}`}
            value={planCode}
            onChange={(e) => {
              setPlanCode(e.target.value);
            }}
          >
            {PLAN_CODES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Select>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={mut.isPending || planCode === row.planCode}
            onClick={() => {
              mut.mutate(planCode);
            }}
          >
            {mut.isPending ? 'Saving…' : 'Save plan change'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GrantCreditDialog({ row }: { row: SubscriptionRow }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [amountUsd, setAmountUsd] = useState('5');
  const [reason, setReason] = useState('');
  const mut = useMutation({
    mutationFn: () =>
      billingApi.grantCredit(client, {
        tenantId: row.tenantId,
        amountUsdMicro: Math.round(Number(amountUsd) * 1_000_000),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      toast.success('Credit granted');
      void qc.invalidateQueries({ queryKey: SUBSCRIPTIONS_QUERY_KEY });
      setOpen(false);
      setReason('');
      setAmountUsd('5');
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Credit failed');
    },
  });

  const usdNumber = Number(amountUsd);
  const valid =
    Number.isFinite(usdNumber) && usdNumber > 0 && reason.trim().length >= 3;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="default"
        size="sm"
        onClick={() => {
          setOpen(true);
        }}
      >
        <Banknote aria-hidden className="mr-1 h-3.5 w-3.5" />
        Credit
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Grant credit to {row.tenantName}</DialogTitle>
          <DialogDescription>
            Inserts a <code>credit_grant</code> row in <code>usage_events</code>.
            The plan-enforcer subtracts credits from metered cost; the daily
            reconciliation reflects them.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`amount-${row.tenantId}`}>Amount (USD)</Label>
            <Input
              id={`amount-${row.tenantId}`}
              type="number"
              min="0.01"
              step="0.01"
              value={amountUsd}
              onChange={(e) => {
                setAmountUsd(e.target.value);
              }}
            />
            <p className="text-xs text-muted-foreground">
              Stored as {Math.round(usdNumber * 1_000_000) || 0} micros.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`reason-${row.tenantId}`}>Reason</Label>
            <Textarea
              id={`reason-${row.tenantId}`}
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              placeholder="e.g. goodwill — onboarding outage 2026-04-22"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={!valid || mut.isPending}
            onClick={() => {
              mut.mutate();
            }}
          >
            {mut.isPending ? 'Granting…' : 'Grant credit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReconTable({ rows }: { rows: readonly { id: string; tenantId: string; tenantName: string; runDate: string; eventKind: string; localTotal: number; stripeTotal: number; driftPct: string; status: string; finishedAt: string | null }[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Run date</TableHead>
          <TableHead>Tenant</TableHead>
          <TableHead>Event</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Drift %</TableHead>
          <TableHead>Finished</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id}>
            <TableCell>{r.runDate}</TableCell>
            <TableCell>
              <Link to={`/tenants/${r.tenantId}`} className="hover:text-foreground">
                {r.tenantName}
              </Link>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">{r.eventKind}</TableCell>
            <TableCell>
              <ReconStatusBadge status={r.status} />
            </TableCell>
            <TableCell className="text-right font-mono text-sm">{r.driftPct}%</TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {r.finishedAt ? formatDateTime(r.finishedAt) : '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function ReconStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'green':
      return <Badge variant="default">green</Badge>;
    case 'drift_warn':
      return <Badge variant="secondary">drift warn</Badge>;
    case 'drift_alert':
      return <Badge variant="destructive">drift alert</Badge>;
    case 'error':
      return <Badge variant="destructive">error</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function SubsSkeleton() {
  return (
    <div className="space-y-2 px-4 py-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

function ReconSkeleton() {
  return (
    <div className="space-y-2 px-4 py-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  );
}
